/**
 * Routeur à fragment d'URL.
 *
 * Le fragment (`#/carte`) et non le chemin (`/carte`), pour deux raisons.
 * D'abord parce qu'un hébergement statique renverrait une erreur 404 sur
 * `/carte` sans règle de réécriture. Ensuite et surtout parce que dans une
 * coque Capacitor, les fichiers sont servis localement : une navigation par
 * chemin n'y fonctionne pas, alors qu'un fragment fonctionne partout.
 *
 * Ajouter une page se fait en trois endroits : une entrée dans ROUTES, une
 * section dans index.html, un module dans ui/pages.
 */
export const ROUTES = {
  home: '#/',
  map: '#/carte',
  sources: '#/sources',
} as const;

export type RouteName = keyof typeof ROUTES;

const DEFAULT_ROUTE: RouteName = 'home';

function routeFromHash(hash: string): RouteName {
  const found = (Object.keys(ROUTES) as RouteName[]).find((name) => ROUTES[name] === hash);
  return found ?? DEFAULT_ROUTE;
}

export interface Router {
  current(): RouteName;
  go(route: RouteName): void;
  onChange(listener: (route: RouteName) => void): void;
  /** Émet la route courante et se met à l'écoute des changements. */
  start(): void;
}

export function createRouter(): Router {
  const listeners = new Set<(route: RouteName) => void>();
  let current: RouteName = routeFromHash(window.location.hash);

  const emit = (): void => {
    for (const listener of listeners) listener(current);
  };

  return {
    current: () => current,

    go(route) {
      if (window.location.hash === ROUTES[route]) return;
      // Modifier le fragment déclenche hashchange, qui fera le reste.
      window.location.hash = ROUTES[route];
    },

    onChange(listener) {
      listeners.add(listener);
    },

    start() {
      window.addEventListener('hashchange', () => {
        const next = routeFromHash(window.location.hash);
        if (next === current) return;
        current = next;
        emit();
      });
      emit();
    },
  };
}
